import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaEnvelope,
  FaPhoneAlt,
  FaPlayCircle,
  FaWhatsapp,
} from "react-icons/fa";

const SUPPORT = {
  phone: "9988777409",
  whatsapp: "919988777409",
  email: "support@billsarthi.com",
  demoUrl: "https://youtube.com/@BillSarthi",
  hours: "Mon-Sat • 10:00 AM - 7:00 PM (IST)",
};

const basicRules = [
  "Name should be between 3 and 30 characters.",
  "Mobile number must be exactly 10 digits.",
  "Password should be between 6 and 20 characters.",
  "Enter the OTP exactly as received.",
];

const signupSteps = [
  "Open Create your account from the login page.",
  "Enter the owner name, mobile number, and password.",
  "Verify the OTP to confirm the mobile number.",
  "Submit the form and sign in to access your account.",
];

const loginSteps = [
  "Enter your registered mobile number.",
  "Enter the correct password.",
  "Click Sign In to open the dashboard.",
  "Use Forgot Password if you need to reset your password.",
];

const commonIssues = [
  "If the OTP is delayed, wait 30 to 60 seconds and try again.",
  "If the password is incorrect, use the password reset flow.",
  "If the mobile number is invalid, enter 10 digits without +91.",
  "If web access is blocked, contact support to verify plan eligibility.",
];

const actionCards = [
  {
    title: "Call Support",
    subtitle: "+91 9988777409",
    caption: "Instant conversation with support executive",
    tone: "#dcfce7",
    accent: "#16a34a",
    icon: <FaPhoneAlt />,
    action: "Call now",
    onClick: () => {
      window.location.href = `tel:${SUPPORT.phone}`;
    },
  },
  {
    title: "WhatsApp Chat",
    subtitle: "Fastest way to get help",
    caption: "Share screenshot, issue details and mobile number",
    tone: "#dbeafe",
    accent: "#0284c7",
    icon: <FaWhatsapp />,
    action: "Open WhatsApp",
    onClick: () => {
      const text = encodeURIComponent(
        "Hi Bill Sarthi Team, I need help with login or signup."
      );
      const opened = window.open(
        `https://wa.me/${SUPPORT.whatsapp}?text=${text}`,
        "_blank",
        "noopener,noreferrer"
      );
      if (!opened) {
        window.location.href = `https://wa.me/${SUPPORT.whatsapp}?text=${text}`;
      }
    },
  },
  {
    title: "Email Support",
    subtitle: "support@billsarthi.com",
    caption: "Best option for sharing detailed issue information",
    tone: "#fef3c7",
    accent: "#d97706",
    icon: <FaEnvelope />,
    action: "Write email",
    onClick: () => {
      const subject = encodeURIComponent("Login/Signup Help | Bill Sarthi");
      const body = encodeURIComponent(
        "Hi Support Team,\n\nI need help with login or signup.\n\nIssue details:\n- \n\nRegistered mobile number:\n\nThanks"
      );
      window.location.href = `mailto:${SUPPORT.email}?subject=${subject}&body=${body}`;
    },
  },
  {
    title: "Watch Demo",
    subtitle: "YouTube walkthrough",
    caption: "A quick visual walkthrough of login and signup steps",
    tone: "#ede9fe",
    accent: "#6d28d9",
    icon: <FaPlayCircle />,
    action: "Open demo",
    onClick: () => {
      const opened = window.open(
        SUPPORT.demoUrl,
        "_blank",
        "noopener,noreferrer"
      );
      if (!opened) window.location.href = SUPPORT.demoUrl;
    },
  },
];

const quickMetrics = [
  { label: "Support Window", value: SUPPORT.hours },
  { label: "Preferred Channel", value: "WhatsApp + Call" },
  { label: "Typical Help Topics", value: "OTP, Login, Signup, Web Access" },
];

const sectionMeta = [
  { title: "Basic Rules", eyebrow: "Before You Start", items: basicRules, kind: "points" },
  { title: "How to Signup", eyebrow: "New User Journey", items: signupSteps, kind: "steps" },
  { title: "How to Login", eyebrow: "Existing User Journey", items: loginSteps, kind: "steps" },
  { title: "Common Issues", eyebrow: "Quick Troubleshooting", items: commonIssues, kind: "points" },
];

const LoginHelpScreenPage = () => {
  const navigate = useNavigate();

  return (
    <Page>
      <Backdrop aria-hidden />
      <GlowOrbOne aria-hidden />
      <GlowOrbTwo aria-hidden />

      <Shell>
        <HeaderBar>
          <BackButton type="button" onClick={() => navigate("/login")}>
            <FaArrowLeft /> Back to login
          </BackButton>
          <HeaderBadge>Bill Sarthi Support Center</HeaderBadge>
        </HeaderBar>

        <HeroPanel>
          <HeroCopy>
            <HeroEyebrow>Account Access Assistance</HeroEyebrow>
            <HeroTitle>Support for login, signup and account access issues.</HeroTitle>
            <HeroText>
              If a customer is facing issues with OTP, password, signup,
              login, or web plan eligibility, this page gives quick access
              to the right support channel.
            </HeroText>

            <HeroActions>
              <PrimaryAction type="button" onClick={actionCards[1].onClick}>
                <FaWhatsapp /> Chat on WhatsApp
              </PrimaryAction>
              <SecondaryAction type="button" onClick={() => (window.location.href = `tel:${SUPPORT.phone}`)}>
                <FaPhoneAlt /> Call Support
              </SecondaryAction>
            </HeroActions>
          </HeroCopy>

          <HeroAside>
            <AsideCard>
              <AsideLabel>Support Availability</AsideLabel>
              <AsideValue>{SUPPORT.hours}</AsideValue>
              <AsideNote>For faster resolution, share the registered mobile number and a screenshot of the issue.</AsideNote>
            </AsideCard>
            <MiniHighlight>
              <MiniHighlightTitle>Fastest Resolution</MiniHighlightTitle>
              <MiniHighlightText>WhatsApp is the fastest channel for sharing screenshots and issue details with the support team.</MiniHighlightText>
            </MiniHighlight>
          </HeroAside>
        </HeroPanel>

        <MetricsGrid>
          {quickMetrics.map((item) => (
            <MetricCard key={item.label}>
              <MetricLabel>{item.label}</MetricLabel>
              <MetricValue>{item.value}</MetricValue>
            </MetricCard>
          ))}
        </MetricsGrid>

        <SectionHeading>
          <h2>Contact Support</h2>
          <p>Choose the channel that best matches the customer issue and urgency.</p>
        </SectionHeading>

        <ContactGrid>
          {actionCards.map((card) => (
            <ContactCard key={card.title} type="button" onClick={card.onClick}>
              <ContactTop>
                <IconBubble tone={card.tone} accent={card.accent}>
                  {card.icon}
                </IconBubble>
                <ContactText>
                  <h3>{card.title}</h3>
                  <strong>{card.subtitle}</strong>
                </ContactText>
              </ContactTop>
              <ContactCaption>{card.caption}</ContactCaption>
              <ContactFooter>
                <span>{card.action}</span>
              </ContactFooter>
            </ContactCard>
          ))}
        </ContactGrid>

        <SectionHeading>
          <h2>Guides and Troubleshooting</h2>
          <p>Structured instructions so the customer can complete signup or login without confusion.</p>
        </SectionHeading>

        <InfoGrid>
          {sectionMeta.map((section) => (
            <SectionCard key={section.title}>
              <SectionEyebrow>{section.eyebrow}</SectionEyebrow>
              <SectionTitle>{section.title}</SectionTitle>

              {section.kind === "steps"
                ? section.items.map((item, index) => (
                    <StepRow key={item}>
                      <StepNumber>{index + 1}</StepNumber>
                      <StepText>{item}</StepText>
                    </StepRow>
                  ))
                : section.items.map((item) => (
                    <PointRow key={item}>
                      <FaCheckCircle />
                      <span>{item}</span>
                    </PointRow>
                  ))}
            </SectionCard>
          ))}
        </InfoGrid>
      </Shell>
    </Page>
  );
};

export default LoginHelpScreenPage;

const Page = styled.div`
  min-height: 100vh;
  position: relative;
  background: #06111d;
  color: #eaf4ff;
  overflow: hidden;
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background:
    linear-gradient(135deg, rgba(6, 17, 29, 0.98), rgba(8, 24, 39, 0.94)),
    radial-gradient(circle at top left, rgba(0, 193, 255, 0.12), transparent 28%),
    radial-gradient(circle at bottom right, rgba(13, 148, 136, 0.14), transparent 30%);
`;

const GlowOrbOne = styled.div`
  position: fixed;
  top: -120px;
  right: -80px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(34, 211, 238, 0.22), transparent 68%);
  filter: blur(12px);
  pointer-events: none;
`;

const GlowOrbTwo = styled.div`
  position: fixed;
  bottom: -140px;
  left: -120px;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(14, 165, 233, 0.18), transparent 70%);
  filter: blur(18px);
  pointer-events: none;
`;

const Shell = styled.div`
  position: relative;
  z-index: 1;
  width: min(1180px, 94vw);
  margin: 0 auto;
  padding: 28px 0 44px;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 22px;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const BackButton = styled.button`
  border: 1px solid rgba(147, 233, 255, 0.26);
  background: rgba(255, 255, 255, 0.06);
  color: #ecf9ff;
  border-radius: 14px;
  padding: 11px 16px;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
  font-weight: 700;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(10px);
`;

const HeaderBadge = styled.div`
  border-radius: 999px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #a7ebff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const HeroPanel = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(280px, 0.55fr);
  gap: 20px;
  padding: 34px;
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.11), rgba(255, 255, 255, 0.04)),
    radial-gradient(circle at top right, rgba(34, 211, 238, 0.12), transparent 28%);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(16px);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 24px;
  }
`;

const HeroCopy = styled.div`
  max-width: 700px;
`;

const HeroEyebrow = styled.div`
  margin-bottom: 10px;
  color: #8de7ff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(30px, 4vw, 52px);
  line-height: 1.04;
  letter-spacing: -0.03em;
`;

const HeroText = styled.p`
  margin: 18px 0 0;
  max-width: 620px;
  color: rgba(234, 244, 255, 0.82);
  font-size: 17px;
  line-height: 1.75;
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
`;

const PrimaryAction = styled.button`
  min-height: 52px;
  padding: 0 18px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(90deg, #00c2ff, #1ddebb);
  color: #032032;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 18px 38px rgba(0, 193, 255, 0.26);
`;

const SecondaryAction = styled.button`
  min-height: 52px;
  padding: 0 18px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.06);
  color: #edf9ff;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
`;

const HeroAside = styled.div`
  display: grid;
  gap: 14px;
`;

const AsideCard = styled.div`
  padding: 22px;
  border-radius: 22px;
  background: rgba(3, 18, 31, 0.54);
  border: 1px solid rgba(147, 233, 255, 0.14);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
`;

const AsideLabel = styled.div`
  color: #9bcde0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const AsideValue = styled.div`
  margin-top: 10px;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.3;
`;

const AsideNote = styled.p`
  margin: 12px 0 0;
  color: rgba(234, 244, 255, 0.7);
  font-size: 14px;
  line-height: 1.65;
`;

const MiniHighlight = styled.div`
  padding: 18px;
  border-radius: 18px;
  background: rgba(10, 30, 46, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const MiniHighlightTitle = styled.div`
  color: #a8edff;
  font-size: 15px;
  font-weight: 800;
`;

const MiniHighlightText = styled.p`
  margin: 8px 0 0;
  color: rgba(234, 244, 255, 0.72);
  font-size: 14px;
  line-height: 1.65;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  padding: 18px 20px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.16);
`;

const MetricLabel = styled.div`
  color: #96cade;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const MetricValue = styled.div`
  margin-top: 10px;
  color: #f3fbff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
`;

const SectionHeading = styled.div`
  margin-top: 28px;

  h2 {
    margin: 0;
    font-size: 28px;
    line-height: 1.15;
  }

  p {
    margin: 8px 0 0;
    color: rgba(234, 244, 255, 0.7);
    font-size: 15px;
    line-height: 1.7;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 1040px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.button`
  width: 100%;
  padding: 20px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.05));
  text-align: left;
  color: #ecf9ff;
  cursor: pointer;
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.2);
`;

const ContactTop = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const IconBubble = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: ${(props) => props.tone};
  color: ${(props) => props.accent};
  font-size: 22px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.24);
`;

const ContactText = styled.div`
  h3 {
    margin: 0;
    font-size: 18px;
  }

  strong {
    display: block;
    margin-top: 6px;
    color: #a8efff;
    font-size: 14px;
    font-weight: 700;
  }
`;

const ContactCaption = styled.p`
  margin: 18px 0 0;
  color: rgba(234, 244, 255, 0.72);
  font-size: 14px;
  line-height: 1.65;
`;

const ContactFooter = styled.div`
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
  color: #f8fdff;
  font-size: 14px;
  font-weight: 800;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-top: 18px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled.section`
  padding: 24px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.05));
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.2);
`;

const SectionEyebrow = styled.div`
  color: #90dbef;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const SectionTitle = styled.h3`
  margin: 10px 0 0;
  font-size: 24px;
  line-height: 1.2;
`;

const PointRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 16px;
  color: rgba(234, 244, 255, 0.84);
  line-height: 1.7;

  svg {
    flex-shrink: 0;
    margin-top: 4px;
    color: #22c55e;
  }
`;

const StepRow = styled.div`
  display: grid;
  grid-template-columns: 38px 1fr;
  gap: 12px;
  align-items: flex-start;
  margin-top: 16px;
`;

const StepNumber = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(0, 193, 255, 0.16);
  border: 1px solid rgba(147, 233, 255, 0.12);
  color: #97ebff;
  font-weight: 800;
`;

const StepText = styled.div`
  color: rgba(234, 244, 255, 0.84);
  line-height: 1.7;
`;
